import { UnauthorizedError } from "../http/errors/unauthorized.error";
import { Router } from "../router/router";
import { AuthStoreService } from "../security/auth-store.service";
import store from "@/store";
import { AppError } from "@/core/common/http/errors/app.error";
import { useNotify } from "@/composables/notify.composable";
import { PasswordExpiredError } from "../security/errors/password-expired.error";

const CHUNK_LOADING_RELOAD_SESSION_KEY = "gparn:chunk-loading-reload";

export class GlobalErrorHandler {
  constructor(private readonly router: Router, private readonly authStoreService: AuthStoreService) {
    this.catchUnhandledRejection();
    this.catchWindowError();
    this.catchRouterError();
  }

  private catchRouterError(): void {
    const notify = useNotify();

    this.router.getInstance().onError((error: unknown) => {
      if (!this.isChunkLoadingFailure(error)) {
        return;
      }

      const hasReloaded = this.tryReloadAfterChunkLoadingFailure();
      if (!hasReloaded) {
        notify.error("La aplicación se ha actualizado. Recarga la página para continuar.");
      }
    });
  }

  private catchUnhandledRejection(): void {
    const notify = useNotify();

    window.addEventListener("unhandledrejection", (promiseRejectionEvent: PromiseRejectionEvent) => {
      promiseRejectionEvent.promise.catch(async (error: unknown) => {
        if (this.isChunkLoadingFailure(error)) {
          const hasReloaded = this.tryReloadAfterChunkLoadingFailure();
          if (!hasReloaded) {
            notify.error("La aplicación se ha actualizado. Recarga la página para continuar.");
          }
          return;
        }

        if (this.isControlledError(error)) {
          await store.dispatch(`saveError`, error);
          return;
        }

        if (error instanceof PasswordExpiredError) {
          await this.redirectChangePassword();
          return;
        }

        if (error instanceof UnauthorizedError) {
          await this.authStoreService.removeToken();
          await this.redirectLogin(error);
          return;
        }

        notify.error(this.getMessage(error));
      });
    });
  }

  private catchWindowError(): void {
    const notify = useNotify();

    window.addEventListener("error", (event: ErrorEvent) => {
      const error: unknown = event.error ?? new Error(event.message);

      if (!this.isChunkLoadingFailure(error)) {
        return;
      }

      const hasReloaded = this.tryReloadAfterChunkLoadingFailure();
      if (!hasReloaded) {
        notify.error("La aplicación se ha actualizado. Recarga la página para continuar.");
      }
    });
  }

  private redirectChangePassword() {
    const currentRoute = this.router.getInstance().currentRoute.value;
    if (currentRoute.name === "Change Expired Password") return;
    return this.router.push("/change-expired-password");
  }

  private redirectLogin(error: UnauthorizedError) {
    const currentRoute = this.router.getInstance().currentRoute.value;
    if (currentRoute.name === "Login") return;

    const failedRoute = error.destinationRoute ? error.destinationRoute : currentRoute;
    const isRoutePrivate = failedRoute.meta.requiresAuth;
    const redirectPath = String(failedRoute.fullPath);
    const queryRedirect = isRoutePrivate ? `?redirects=${redirectPath}` : "";
    return this.router.push(`/login${queryRedirect}`);
  }

  private getMessage(error: unknown): string {
    if (error instanceof AppError) {
      return error.clientMessage ? error.clientMessage : "Ha ocurrido un error inesperado";
    }

    if (error instanceof Error && error.message) {
      return error.message;
    }

    return "Ha ocurrido un error inesperado";
  }

  private isControlledError(error: unknown): boolean {
    if (!(error instanceof AppError)) return false;
    return error.code === 500 || error.code === 403 || error.code === 404;
  }

  private isChunkLoadingFailure(error: unknown): boolean {
    if (!(error instanceof Error)) return false;

    const errorName = error.name.toLowerCase();
    const message = error.message.toLowerCase();

    if (errorName.includes("chunkloaderror")) return true;
    if (message.includes("loading chunk") && message.includes("failed")) return true;
    if (message.includes("loading css chunk") && message.includes("failed")) return true;
    if (message.includes("failed to fetch dynamically imported module")) return true;
    if (message.includes("importing a module script failed")) return true;

    return false;
  }

  private tryReloadAfterChunkLoadingFailure(): boolean {
    if (this.hasReloadedAfterChunkLoadingFailure()) {
      return false;
    }

    this.saveReloadAfterChunkLoadingFailure();
    window.location.reload();
    return true;
  }

  private hasReloadedAfterChunkLoadingFailure(): boolean {
    try {
      return window.sessionStorage.getItem(CHUNK_LOADING_RELOAD_SESSION_KEY) === "true";
    } catch {
      return true;
    }
  }

  private saveReloadAfterChunkLoadingFailure(): void {
    try {
      window.sessionStorage.setItem(CHUNK_LOADING_RELOAD_SESSION_KEY, "true");
    } catch {
      return;
    }
  }
}
